import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { PlusIcon, FunnelIcon, TableCellsIcon, Squares2X2Icon, ArrowDownTrayIcon, ArrowUpTrayIcon, MapIcon } from '@heroicons/react/24/outline';
import { toast } from 'react-hot-toast';
import { updateLead, deleteLead } from '../../services/lead.service';
import BulkActionsBar from '../../components/leads/BulkActionsBar';
import { useActivityLogger } from '../../hooks/useActivityLogger';
import { Lead } from '../../types/lead.types';
import { useAuth } from '../../contexts/AuthContext';
import { useProject } from '../../contexts/ProjectContext';
import LeadGrid from '../../components/leads/LeadGrid';
import LeadTable from '../../components/leads/LeadTable';
import LeadFiltersPanel from '../../components/leads/LeadFiltersPanel';
import LeadDetailModal from '../../components/leads/LeadDetailModal';
import LeadMap from '../../components/leads/LeadMap';
import { query, collection, where, getDocs, orderBy, limit, startAfter, DocumentSnapshot } from 'firebase/firestore';
import { db } from '../../config/firebase';
import LoadingSpinner from '../../components/shared/LoadingSpinner';
import { exportLeadsToCSV } from '../../utils/leadExport';
import { appointmentService } from '../../services/appointment.service';
import { Appointment } from '../../types/schedule.types';
import { useProjectSegments } from '../../hooks/useProjectSegments';
import { LeadImportModal } from '../../components/leads/LeadImportModal';
import { buildExportCsv, downloadCsv, exportFilename } from '../../utils/leadCsvExport';
import { CSV_EXPORT_TEMPLATES } from '../../config/csvTemplates';

// Define custom filter interface since LeadFilters expects arrays
interface CustomLeadFilters {
  search: string;
  status?: string;
  determination?: string;
  dateRange?: { start: Date | null; end: Date | null };
  schoolSegmentId?: string;
}

const PAGE_SIZE = 100;

const LeadsPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { currentUser, userProfile } = useAuth();
  const { projects, selectedProjectId, selectedProject, setSelectedProjectId } = useProject();
  const { segments } = useProjectSegments(selectedProjectId);
  const { logActivity: logAct } = useActivityLogger();
  const [leads, setLeads] = useState<Lead[]>([]);
  const [filteredLeads, setFilteredLeads] = useState<Lead[]>([]);
  const [selectedLead, setSelectedLead] = useState<Lead | null>(null);
  const [viewMode, setViewMode] = useState<'grid' | 'table' | 'map'>('grid');
  const [showFilters, setShowFilters] = useState(false);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  // lastDoc lives in a ref so updating it after a "Load More" doesn't change
  // the loadLeads callback identity and re-trigger the load-on-mount effect
  // (which would reset us back to page 1).
  const lastDocRef = useRef<DocumentSnapshot | null>(null);
  const [hasMore, setHasMore] = useState(false);
  const [filters, setFilters] = useState<CustomLeadFilters>({
    search: '',
    dateRange: { start: null, end: null }
  });
  // Bulk-selection state for the spreadsheet table view.
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [showImport, setShowImport] = useState(false);

  // Client-format (HubSpot) export of the currently filtered leads.
  const handleExportHubspot = () => {
    const template = CSV_EXPORT_TEMPLATES[0];
    if (!template || filteredLeads.length === 0) return;
    const csv = buildExportCsv(filteredLeads, template, segments);
    downloadCsv(csv, exportFilename(template.filenamePrefix));
  };

  // Signature Accountability PDF (signature projects only): every signed lead
  // in the current filter, with signature image + collector GPS + timestamp.
  const handleSignatureReport = async () => {
    if (!selectedProject) return;
    const signed = filteredLeads.filter((l) => l.signature);
    if (signed.length === 0) {
      toast.error('No signed leads in the current filter.');
      return;
    }
    const { buildSignatureReportPdf } = await import('../../utils/signatureReportPdf');
    buildSignatureReportPdf(signed, selectedProject);
    toast.success(`Signature report generated (${signed.length} households).`);
  };

  const toggleSelect = useCallback((id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const toggleSelectAll = useCallback(() => {
    setSelectedIds((prev) => {
      // If everything in the filtered view is already selected, clear it.
      // Otherwise select every visible (filtered) lead.
      const visibleIds = filteredLeads.map((l) => l.id);
      const allSelected = visibleIds.length > 0 && visibleIds.every((id) => prev.has(id));
      return allSelected ? new Set() : new Set(visibleIds);
    });
  }, [filteredLeads]);

  const clearSelection = useCallback(() => setSelectedIds(new Set()), []);

  // Inline patch handler for the spreadsheet table. Optimistically updates
  // the source-of-truth `leads` array; the existing filter pipeline will
  // re-run and refresh `filteredLeads`.
  const handleLeadPatch = useCallback(
    async (id: string, patch: Partial<Lead>) => {
      const previous = leads.find((l) => l.id === id);
      // Optimistic update first so the cell reflects the change immediately.
      setLeads((prev) => prev.map((l) => (l.id === id ? { ...l, ...patch } : l)));
      try {
        await updateLead(id, patch);
      } catch (err) {
        console.error('Failed to update lead inline:', err);
        toast.error('Failed to update lead');
        // Roll back.
        if (previous) {
          setLeads((prev) => prev.map((l) => (l.id === id ? previous : l)));
        }
        throw err;
      }
    },
    [leads]
  );

  // Bulk apply patch to every selected lead.
  const handleBulkUpdate = useCallback(
    async (updates: Partial<Lead>) => {
      const ids = Array.from(selectedIds);
      try {
        await Promise.all(ids.map((id) => updateLead(id, updates)));
        setLeads((prev) =>
          prev.map((l) => (selectedIds.has(l.id) ? { ...l, ...updates } : l))
        );
        toast.success(`Updated ${ids.length} lead${ids.length === 1 ? '' : 's'}`);
        clearSelection();
      } catch (err) {
        console.error('Bulk update failed:', err);
        toast.error('Failed to update some leads');
      }
    },
    [selectedIds, clearSelection]
  );

  const handleBulkDelete = useCallback(async () => {
    const ids = Array.from(selectedIds);
    try {
      await Promise.all(ids.map((id) => deleteLead(id)));
      setLeads((prev) => prev.filter((l) => !selectedIds.has(l.id)));
      toast.success(`Deleted ${ids.length} lead${ids.length === 1 ? '' : 's'}`);
      clearSelection();
    } catch (err) {
      console.error('Bulk delete failed:', err);
      toast.error('Failed to delete some leads');
    }
  }, [selectedIds, clearSelection]);

  // Handle URL projectId parameter
  useEffect(() => {
    const urlProjectId = searchParams.get('projectId');
    if (urlProjectId && projects.length > 0) {
      const validProject = projects.find(p => p.id === urlProjectId);
      if (validProject && urlProjectId !== selectedProjectId) {
        setSelectedProjectId(urlProjectId);
      }
    }
  }, [searchParams, projects, selectedProjectId, setSelectedProjectId]);

  // Load leads when project changes (with pagination). All roles see every
  // lead on the selected project — field users included — so they can edit
  // any lead, not just ones they captured themselves. Determination editing
  // remains gated separately in LeadDeterminationTab / BulkActionsBar.
  const loadLeads = useCallback(async (loadMore = false) => {
    if (!currentUser || !userProfile || !selectedProjectId) {
      setLeads([]);
      setFilteredLeads([]);
      setLoading(false);
      return;
    }

    if (loadMore) {
      setLoadingMore(true);
    } else {
      setLoading(true);
      lastDocRef.current = null;
    }

    try {
      // Fetch ALL of the project's leads, paging through in PAGE_SIZE batches.
      // A single capped page hid every lead past the first 100 from the list,
      // the header counts, exports, AND the recap-import matcher (which
      // matches recap rows against the loaded leads only).
      let cursor: DocumentSnapshot | null = loadMore ? lastDocRef.current : null;
      const newLeads: Lead[] = [];
      // eslint-disable-next-line no-constant-condition
      while (true) {
        const leadsQuery = cursor
          ? query(
              collection(db, 'leads'),
              where('projectId', '==', selectedProjectId),
              orderBy('createdAt', 'desc'),
              startAfter(cursor),
              limit(PAGE_SIZE)
            )
          : query(
              collection(db, 'leads'),
              where('projectId', '==', selectedProjectId),
              orderBy('createdAt', 'desc'),
              limit(PAGE_SIZE)
            );

        const snapshot = await getDocs(leadsQuery);
        newLeads.push(...snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        } as Lead)));

        if (snapshot.docs.length > 0) cursor = snapshot.docs[snapshot.docs.length - 1];
        if (snapshot.docs.length < PAGE_SIZE) break;
      }

      lastDocRef.current = cursor;
      setHasMore(false);

      if (loadMore) {
        setLeads(prev => [...prev, ...newLeads]);
      } else {
        setLeads(newLeads);
      }
    } catch (error) {
      console.error('Error loading leads:', error);
      toast.error('Failed to load leads');
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  }, [currentUser, userProfile, selectedProjectId]);

  // Load leads when selected project changes
  useEffect(() => {
    if (selectedProjectId) {
      loadLeads();
    }
  }, [selectedProjectId, loadLeads]);

  const applyFilters = useCallback(() => {
    let filtered = [...leads];

    // Search filter
    if (filters.search) {
      const searchLower = filters.search.toLowerCase();
      filtered = filtered.filter(lead => 
        lead.parentName?.toLowerCase().includes(searchLower) ||
        lead.email?.toLowerCase().includes(searchLower) ||
        lead.phone?.includes(filters.search) ||
        lead.address?.toLowerCase().includes(searchLower) ||
        lead.city?.toLowerCase().includes(searchLower) ||
        lead.zipCode?.includes(filters.search)
      );
    }

    // Status filter
    if (filters.status) {
      filtered = filtered.filter(lead => lead.status === filters.status);
    }

    // Determination filter
    if (filters.determination) {
      filtered = filtered.filter(lead => lead.determination === filters.determination);
    }

    // School / campus filter — matches the lead's segment or any child override
    if (filters.schoolSegmentId) {
      filtered = filtered.filter(lead =>
        lead.schoolSegmentId === filters.schoolSegmentId ||
        (lead.children || []).some((c: any) => c.schoolSegmentId === filters.schoolSegmentId)
      );
    }

    // Date range filter
    if (filters.dateRange?.start || filters.dateRange?.end) {
      filtered = filtered.filter(lead => {
        const leadDate = lead.createdAt?.toDate();
        if (!leadDate) return false;

        if (filters.dateRange?.start && leadDate < filters.dateRange.start) return false;
        if (filters.dateRange?.end && leadDate > filters.dateRange.end) return false;

        return true;
      });
    }

    setFilteredLeads(filtered);
  }, [leads, filters]);

  useEffect(() => {
    applyFilters();
  }, [applyFilters]);

  const handleFilterChange = (newFilters: any) => {
    setFilters({
      search: newFilters.search || '',
      status: newFilters.status?.[0],
      determination: newFilters.determination?.[0],
      dateRange: newFilters.dateRange || { start: null, end: null },
      schoolSegmentId: newFilters.schoolSegmentId
    });
  };

  const handleLeadUpdate = async (updatedLead: Lead) => {
    try {
      await updateLead(updatedLead.id, updatedLead);
      
      setLeads(prevLeads => 
        prevLeads.map(lead => lead.id === updatedLead.id ? updatedLead : lead)
      );
      setFilteredLeads(prevLeads => 
        prevLeads.map(lead => lead.id === updatedLead.id ? updatedLead : lead)
      );
      setSelectedLead(updatedLead);
      logAct({ action: 'lead_updated', entityType: 'lead', entityId: updatedLead.id, entityName: updatedLead.parentName });

      toast.success('Lead updated successfully');
    } catch (error) {
      console.error('Error updating lead:', error);
      toast.error('Failed to update lead');
    }
  };

  const handleLeadNavigate = (direction: 'prev' | 'next') => {
    if (!selectedLead) return;
    
    const currentIndex = filteredLeads.findIndex(lead => lead.id === selectedLead.id);
    if (currentIndex === -1) return;
    
    const newIndex = direction === 'prev' ? currentIndex - 1 : currentIndex + 1;
    if (newIndex >= 0 && newIndex < filteredLeads.length) {
      setSelectedLead(filteredLeads[newIndex]);
    }
  };

  const [exporting, setExporting] = useState(false);
  const handleExportCSV = async () => {
    if (exporting || filteredLeads.length === 0) return;
    setExporting(true);
    try {
      // Appointments live in a separate collection keyed by projectId. Pull
      // them once per project that appears in the current filtered set so
      // the export can include schedule history without N+1 fetches.
      const projectIds = Array.from(
        new Set(filteredLeads.map((l) => l.projectId).filter(Boolean))
      );
      const results = await Promise.all(
        projectIds.map((pid) =>
          appointmentService.getAppointmentsByProject(pid).catch((err) => {
            console.error('Failed to load appointments for export', pid, err);
            return [] as Appointment[];
          })
        )
      );
      const filteredLeadIds = new Set(filteredLeads.map((l) => l.id));
      const appointmentsByLeadId: Record<string, Appointment[]> = {};
      for (const list of results) {
        for (const apt of list) {
          if (!apt.leadId || !filteredLeadIds.has(apt.leadId)) continue;
          (appointmentsByLeadId[apt.leadId] ||= []).push(apt);
        }
      }
      exportLeadsToCSV(
        filteredLeads,
        `leads_export_${new Date().toISOString().split('T')[0]}.csv`,
        appointmentsByLeadId
      );
    } catch (err) {
      console.error('Lead export failed:', err);
      toast.error('Failed to export leads');
    } finally {
      setExporting(false);
    }
  };

  const canNavigate = {
    prev: selectedLead ? filteredLeads.findIndex(l => l.id === selectedLead.id) > 0 : false,
    next: selectedLead ? filteredLeads.findIndex(l => l.id === selectedLead.id) < filteredLeads.length - 1 : false
  };

  if (loading && !projects.length) {
    return (
      <div className="flex justify-center items-center h-64">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  if (projects.length === 0 && !loading) {
    return (
      <div className="p-6">
        <h1 className="text-2xl font-bold text-gray-900 mb-6">Leads</h1>
        <div className="bg-white rounded-lg shadow p-8 text-center">
          <p className="text-gray-500 text-lg">No projects assigned to you.</p>
          <p className="text-gray-400 mt-2">Contact your administrator to get access to a project.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
        <div className="flex flex-col sm:flex-row sm:items-center gap-4">
          <h1 className="text-2xl font-bold text-gray-900">Leads</h1>
          {/* Project Selector - synced with global nav selector */}
          {projects.length > 0 && (
            <div className="flex items-center">
              <label htmlFor="project-select" className="sr-only">Select Project</label>
              <select
                id="project-select"
                value={selectedProjectId || ''}
                onChange={(e) => setSelectedProjectId(e.target.value)}
                className="block w-full sm:w-64 px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 text-sm"
              >
                {projects.length === 0 && (
                  <option value="">No projects available</option>
                )}
                {projects.map((project) => (
                  <option key={project.id} value={project.id}>
                    {project.name}
                  </option>
                ))}
              </select>
            </div>
          )}
        </div>
        <div className="flex items-center space-x-4">
          <button
            onClick={handleExportCSV}
            className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
            disabled={filteredLeads.length === 0 || exporting}
          >
            <ArrowDownTrayIcon className="h-4 w-4 mr-2" />
            {exporting ? 'Exporting...' : 'Export CSV'}
          </button>
          <button
            onClick={handleExportHubspot}
            className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
            disabled={filteredLeads.length === 0}
            title="Export filtered leads in the client's HubSpot import format"
          >
            <ArrowDownTrayIcon className="h-4 w-4 mr-2" />
            HubSpot CSV
          </button>
          <button
            onClick={() => setShowImport(true)}
            className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
            title="Import a client recap CSV to update leads"
          >
            <ArrowUpTrayIcon className="h-4 w-4 mr-2" />
            Import Recap
          </button>
          {selectedProject?.settings?.signatureCapture && (
            <button
              onClick={handleSignatureReport}
              className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
              disabled={filteredLeads.length === 0}
              title="PDF accountability report: signature image, collector GPS, and timestamp for every signed household in the current filter"
            >
              <ArrowDownTrayIcon className="h-4 w-4 mr-2" />
              Signature Report
            </button>
          )}
          <button
            onClick={() => navigate('/leads/new')}
            className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
          >
            <PlusIcon className="h-5 w-5 mr-2" />
            Add Lead
          </button>
        </div>
      </div>

      {/* Toolbar */}
      <div className="bg-white shadow rounded-lg mb-6">
        <div className="px-4 py-3 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <button
                onClick={() => setShowFilters(!showFilters)}
                className={`inline-flex items-center px-3 py-2 border rounded-md text-sm font-medium ${
                  showFilters
                    ? 'border-indigo-500 text-indigo-600 bg-indigo-50'
                    : 'border-gray-300 text-gray-700 bg-white hover:bg-gray-50'
                }`}
              >
                <FunnelIcon className="h-4 w-4 mr-2" />
                Filters
              </button>
              <span className="text-sm text-gray-500">
                {filteredLeads.length} of {leads.length} leads ({filteredLeads.reduce((sum, l) => sum + (l.children?.length || 0), 0)} of {leads.reduce((sum, l) => sum + (l.children?.length || 0), 0)} students)
              </span>
            </div>
            <div className="flex items-center space-x-2">
              <button
                onClick={() => setViewMode('grid')}
                className={`p-2 rounded ${
                  viewMode === 'grid'
                    ? 'bg-gray-100 text-gray-900'
                    : 'text-gray-400 hover:text-gray-500'
                }`}
                title="Grid view"
              >
                <Squares2X2Icon className="h-5 w-5" />
              </button>
              <button
                onClick={() => setViewMode('table')}
                className={`p-2 rounded ${
                  viewMode === 'table'
                    ? 'bg-gray-100 text-gray-900'
                    : 'text-gray-400 hover:text-gray-500'
                }`}
                title="Table view"
              >
                <TableCellsIcon className="h-5 w-5" />
              </button>
              <button
                onClick={() => setViewMode('map')}
                className={`p-2 rounded ${
                  viewMode === 'map'
                    ? 'bg-gray-100 text-gray-900'
                    : 'text-gray-400 hover:text-gray-500'
                }`}
                title="Map view"
              >
                <MapIcon className="h-5 w-5" />
              </button>
            </div>
          </div>
        </div>

        {/* Filters Panel */}
        {showFilters && (
          <div className="px-4 py-4 bg-gray-50">
            <LeadFiltersPanel
              segments={segments}
              filters={{
                search: filters.search,
                status: filters.status ? [filters.status] : [],
                determination: filters.determination ? [filters.determination] : [],
                dateRange: filters.dateRange || { start: null, end: null },
                schoolSegmentId: filters.schoolSegmentId
              }}
              onFiltersChange={handleFilterChange}
            />
          </div>
        )}
      </div>

      {/* Bulk actions bar — only visible when rows are selected in the table view */}
      {viewMode === 'table' && selectedIds.size > 0 && (
        <BulkActionsBar
          selectedCount={selectedIds.size}
          onBulkUpdate={handleBulkUpdate}
          onBulkDelete={handleBulkDelete}
          onDeselectAll={clearSelection}
        />
      )}

      {/* Leads Display */}
      {filteredLeads.length === 0 ? (
        <div className="text-center py-12">
          <p className="text-gray-500">No leads found matching your filters.</p>
        </div>
      ) : viewMode === 'grid' ? (
        <LeadGrid
          leads={filteredLeads}
          onLeadSelect={setSelectedLead}
          validationDeadlineDays={selectedProject?.settings?.validationDeadlineDays || 2}
          segments={segments}
        />
      ) : viewMode === 'table' ? (
        <>
          {/* Spreadsheet table for desktop */}
          <LeadTable
            leads={filteredLeads}
            onLeadSelect={setSelectedLead}
            validationDeadlineDays={selectedProject?.settings?.validationDeadlineDays || 2}
            onLeadPatch={handleLeadPatch}
            onContactLogged={() => loadLeads()}
            selectedIds={selectedIds}
            onToggleSelect={toggleSelect}
            onToggleSelectAll={toggleSelectAll}
            segments={segments}
          />
          {/* Mobile fallback: table view is desktop-only, show grid instead */}
          <div className="md:hidden">
            <LeadGrid
              leads={filteredLeads}
              onLeadSelect={setSelectedLead}
              validationDeadlineDays={selectedProject?.settings?.validationDeadlineDays || 2}
              segments={segments}
            />
          </div>
        </>
      ) : (
        <LeadMap
          leads={filteredLeads}
          onLeadSelect={setSelectedLead}
        />
      )}

      {/* Load More Button */}
      {hasMore && !loading && (
        <div className="flex justify-center py-6">
          <button
            onClick={() => loadLeads(true)}
            disabled={loadingMore}
            className="px-6 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
          >
            {loadingMore ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                Loading...
              </>
            ) : (
              <>Load More ({leads.length} loaded)</>
            )}
          </button>
        </div>
      )}

      {/* Import Recap Modal */}
      {showImport && (
        <LeadImportModal
          isOpen={showImport}
          onClose={() => setShowImport(false)}
          leads={leads}
          onApplied={() => loadLeads()}
        />
      )}

      {/* Lead Detail Modal */}
      {selectedLead && (
        <LeadDetailModal
          lead={selectedLead}
          onClose={() => setSelectedLead(null)}
          onUpdate={handleLeadUpdate}
          onNavigate={handleLeadNavigate}
          canNavigate={canNavigate}
          validationDeadlineDays={selectedProject?.settings?.validationDeadlineDays || 2}
        />
      )}
    </div>
  );
};

export default LeadsPage;